import { Container, Section } from '../layout';
import { Card, Icon } from '../ui';

export const ApproachSection = () => {
  const approachSteps = [
    {
      step: '01',
      title: 'Understanding & Analysis',
      description: 'We begin by thoroughly understanding your specific legal needs, business objectives, and challenges. Our team conducts comprehensive analysis to identify the most effective legal strategies.',
      icon: 'lightbulb',
      features: [
        'Initial consultation and needs assessment',
        'Comprehensive legal analysis',
        'Risk identification and evaluation',
        'Strategic planning and roadmap development'
      ]
    },
    {
      step: '02',
      title: 'Strategic Planning & Execution',
      description: 'Based on our analysis, we develop tailored legal strategies and execute them with precision. Our experienced team ensures every aspect is handled with attention to detail and professional excellence.',
      icon: 'briefcase',
      features: [
        'Customized legal strategy development',
        'Detailed execution planning',
        'Resource allocation and timeline management',
        'Regular progress monitoring and updates'
      ]
    },
    {
      step: '03',
      title: 'Resolution & Ongoing Support',
      description: 'We work diligently to achieve optimal outcomes for our clients while providing ongoing support and guidance. Our commitment extends beyond case resolution to ensure long-term success.',
      icon: 'check',
      features: [
        'Focused resolution and outcome achievement',
        'Post-resolution support and guidance',
        'Ongoing legal advisory services',
        'Long-term relationship building'
      ]
    }
  ];

  return (
    <Section id="approach" padding="xl" className="bg-background-light">
      <Container>
        <div className="text-center mb-16">
          <h2 className="heading-lg text-primary-navy mb-6">
            Our Approach
          </h2>
          <p className="body-lg text-text-medium max-w-3xl mx-auto">
            Our systematic three-stage approach ensures comprehensive legal solutions tailored to each client's unique needs, 
            delivering exceptional results through strategic planning and meticulous execution.
          </p>
        </div>

        {/* Timeline/Process Flow */}
        <div className="relative">
          {/* Desktop Timeline Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-accent-gold/30"></div>
          
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {approachSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Mobile Timeline Line */}
                {index < approachSteps.length - 1 && (
                  <div className="lg:hidden absolute left-6 top-20 w-0.5 h-16 bg-accent-gold/30"></div>
                )}
                
                <Card className="p-8 h-full bg-background-white relative">
                  {/* Step Number Circle */}
                  <div className="absolute -top-6 left-8 lg:left-1/2 lg:-translate-x-1/2 w-12 h-12 bg-accent-gold rounded-full flex items-center justify-center">
                    <span className="text-background-white font-bold text-lg">
                      {step.step}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className="flex justify-center mb-6 mt-4">
                    <div className="w-16 h-16 bg-primary-navy/10 rounded-full flex items-center justify-center">
                      <Icon name={step.icon as any} size="xl" className="text-primary-navy" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center mb-6">
                    <h3 className="heading-md text-primary-navy mb-4">
                      {step.title}
                    </h3>
                    <p className="body-base text-text-medium">
                      {step.description}
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3">
                    {step.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start">
                        <Icon 
                          name="check" 
                          size="sm" 
                          className="text-accent-gold mr-3 mt-0.5 flex-shrink-0" 
                        />
                        <span className="body-sm text-text-medium">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Our Approach */}
        <div className="mt-16">
          <div className="bg-background-white rounded-lg p-8">
            <h3 className="heading-md text-primary-navy mb-8 text-center">
              Benefits of Our Approach
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="users" size="lg" className="text-accent-gold" />
                </div>
                <h4 className="font-semibold text-primary-navy mb-2">Client-Centric</h4>
                <p className="body-sm text-text-medium">
                  Every strategy is tailored to your specific needs and objectives
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="star" size="lg" className="text-accent-gold" />
                </div>
                <h4 className="font-semibold text-primary-navy mb-2">Proven Results</h4>
                <p className="body-sm text-text-medium">
                  Track record of successful outcomes across diverse legal matters
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="clock" size="lg" className="text-accent-gold" />
                </div>
                <h4 className="font-semibold text-primary-navy mb-2">Timely Delivery</h4>
                <p className="body-sm text-text-medium">
                  Efficient processes ensuring timely resolution of legal matters
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="scale" size="lg" className="text-accent-gold" />
                </div>
                <h4 className="font-semibold text-primary-navy mb-2">Ethical Practice</h4>
                <p className="body-sm text-text-medium">
                  Unwavering commitment to ethical standards and professional integrity
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <p className="body-lg text-text-medium mb-6">
            Ready to experience our comprehensive approach to legal solutions?
          </p>
          <button 
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="btn-base btn-primary"
          >
            Start Your Legal Journey
          </button>
        </div>
      </Container>
    </Section>
  );
};